import { dateValidatorByFormat } from '@common-fe/common-fe';
import { useMutation, useQueryClient } from '@common-fe/common-fe';
import _ from 'lodash';

// import dayjs from 'dayjs';
import { api, mock } from '@/api';
import {
  DEFAULT_DATE_FORMAT,
  EMPTY_FIELD_VALUE,
  NON_ASSIGNABLE_VALUE,
  NON_ASSIGNABLE_VALUE_PAYLOAD,
  YES_KEY,
} from '@/common/constants';
import PATHS from '@/common/paths';
import { API_URL } from '@/dtos';
import type { IOrganizationInfoDto } from '@/dtos/models/IOrganizationInfoDto';
import { PaymentMethodType } from '@/modules/employee/employee.types';
import {
  CommunicationCategoryPayload,
  CommunicationChannelPayload,
  StatementFeePaidByType,
  StatementSettings,
  StatementType,
} from '@/modules/employer/components/SetupEmployer/Communication/Communication.types';
import {
  AccountUsagePayload,
  daysOfWeek,
  FrequencyOptions,
} from '@/modules/employer/components/SetupEmployer/FinancialDetails/BankInformation/AccountUsage/AccountUsage.types';
import { PlansRenewalValues } from '@/modules/employer/components/SetupEmployer/SetupEmployer.types';
import { EmployerStatus } from '@/modules/employer/employer.constants';
import { QUERY_KEY } from '@/modules/employer/hooks/useEmployersList.query';
import { AllowedPaymentMethodConfigPayload, EmployerDto, PartnerMobileApp } from '@/modules/employer/types';
import { getYesNoBooleanValue } from '@/utils';
import { yesOrNoToBoolean } from '@/utils/handlers';

import { CustomReportsPayload } from '../AdminDashboard/CustomReports/stores/useCustomReports.store';
import { MetricsPayload } from '../AdminDashboard/Metrics/stores/useMetrics.store';
import { EmailConfigurationPayload } from '../Communication/EmailConfiguration/stores/useEmailConfiguration.store';
import { LinkManagementPayload } from '../LinkManagement/useLinkManagementStore.store';
import { MobileAppCustomizationPayload } from '../MobileAppCustomization/store/useMobileAppCustomization.store';
import {
  AddressPayload,
  AIAdjudication,
  BankInfo,
  CardSetupPayload,
  ClaimsSettings,
  EmployeeIdPayload,
  EmployerPayload,
  ExpressSetupLinksPayload,
  FileManagerSettings,
  FraudThreshold,
  FundingModelPayload,
  GeneralInformationPayload,
  HelpInfoPayload,
  MultiFactorAuthenticationValues,
  OrphanConfigurationPayload,
  TechnicalSettings,
} from '../SetupEmployer.types';

const getDateIsoString = (dateValue?: string | null) => {
  // for now work only with date format 'MM/DD/YYYY'
  if (!dateValue) return null;
  const [month, day, year] = dateValue.split('/');
  const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
  const isoString = date.toISOString();

  return isoString;
};

const getBankId = (banks: BankInfo[], value: string) => banks.find((bank: BankInfo) => bank.id === value)?.id;

const getExternalAccountPayload = (storedValue?: string, banksList?: BankInfo[], serverData?: string) => {
  const value =
    storedValue && storedValue !== NON_ASSIGNABLE_VALUE && banksList?.length
      ? getBankId(banksList, storedValue)
      : undefined;
  if (value) {
    return value;
  }

  if (serverData && _.toNumber(serverData) > 0) {
    return serverData;
  }
};

mock.onPost(PATHS.EMPLOYER)?.reply(200, {});

const COUNTRY_ID = 1;
const DAILY_FREQUENCY = 1;
const ADDRESS_TYPE = 'PO_BOX';

const getFormattedAccountUsage = (store: AccountUsagePayload, banksList: BankInfo[], data?: EmployerDto) => ({
  accounts: {
    ...(store.administratorFeesBank && store.administratorFeesBank !== NON_ASSIGNABLE_VALUE
      ? { external_administrator_fees_account: getBankId(banksList, store.administratorFeesBank) }
      : {}),
    ...(store.administratorFeesBank && store.administratorFeesBank === NON_ASSIGNABLE_VALUE
      ? { external_administrator_fees_account: NON_ASSIGNABLE_VALUE_PAYLOAD }
      : {}),
    ...(store.payrollContributionBank && store.payrollContributionBank === NON_ASSIGNABLE_VALUE
      ? {
          external_payroll_contributions_account: NON_ASSIGNABLE_VALUE_PAYLOAD,
        }
      : {}),
    external_replenishment_account: getExternalAccountPayload(
      store.fundingReplenishmentBank,
      banksList,
      data?.normalProperties?.externalReplenishmentAccount
    ),
    external_payroll_contributions_account: getExternalAccountPayload(
      store.payrollContributionBank,
      banksList,
      data?.normalProperties?.externalPayrollContributionsAccount
    ),
    external_push_return_account: getExternalAccountPayload(
      store.externalPushReturnAccount,
      banksList,
      data?.normalProperties?.externalPushReturnAccount
    ),
    external_balance_adjustment_account: getExternalAccountPayload(
      store.balanceAdjustmentBank,
      banksList,
      data?.normalProperties?.externalBalanceAdjustmentAccount
    ),
  },
  methods: {
    external_administrator_fees_account_method:
      store.administratorFeesMethod || data?.overridableProperties?.externalAdministratorFeesAccountMethod,
    external_payroll_contributions_account_method:
      store.payrollContributionMethod ||
      data?.overridableProperties?.externalPayrollContributionsAccountMethod,
    external_replenishment_account_method:
      store.fundingReplenishmentMethod || data?.overridableProperties?.externalReplenishmentAccountMethod,
    external_balance_adjustment_account_method:
      store.balanceAdjustmentMethod || data?.overridableProperties?.externalBalanceAdjustmentAccountMethod,
  },
});
const URL_PREFIX = '.com/';
interface ReimbursementPayload {
  is_reimbursement_allowed?: boolean;
  allowed_payment_methods?: PaymentMethodType[];
  allowed_payment_method_configs?: AllowedPaymentMethodConfigPayload[];
  is_bill_pay_allowed?: boolean;
}

interface Sections {
  reports?: Partial<CustomReportsPayload>;
  metrics?: Partial<MetricsPayload>;
}

export const generalInformationPayload = (
  banksList: BankInfo[],
  generalInfoData: GeneralInformationPayload | null,
  address: AddressPayload | null,
  employer: EmployerPayload | null,
  communicationChannelStore: Partial<CommunicationChannelPayload>,
  communicationCategoryStore: CommunicationCategoryPayload,
  accountUsageStore: AccountUsagePayload,
  data?: EmployerDto,
  employeeId?: EmployeeIdPayload | null,
  fundingModelPayload?: FundingModelPayload | null,
  cardsSetup?: CardSetupPayload | null,
  hasAccessToBankInfo?: boolean,
  reimbursementPayload?: ReimbursementPayload,
  helpInfo?: HelpInfoPayload | null,
  primaryContactId?: string | number | null,
  technicalSettings?: TechnicalSettings,
  fileManagerSettings?: FileManagerSettings,
  emailConfiguration?: EmailConfigurationPayload,
  statementsSettingsStore?: StatementSettings,
  aiAdjudicationStore?: AIAdjudication,
  MultiFactorAuthenticationStore?: MultiFactorAuthenticationValues,
  mobileAppCustomizationState?: MobileAppCustomizationPayload,
  claimsSettings?: ClaimsSettings,
  orphanConfiguration?: Partial<OrphanConfigurationPayload>,
  linkManagement?: LinkManagementPayload,
  expressSetupLinks?: ExpressSetupLinksPayload,
  sections?: Sections,
  plansRenewalStore?: PlansRenewalValues,
  fraudThresholdStore?: FraudThreshold
) => {
  const { reports, metrics } = sections || {};
  const allowAdminContribution = accountUsageStore.allowAdminContribution
    ? getYesNoBooleanValue(accountUsageStore.allowAdminContribution)
    : data?.overridableProperties?.allowAdminContribution;
  const allowAssumedContribution =
    accountUsageStore?.allowAssumedContribution || data?.overridableProperties?.allowAssumedContribution;
  const allowDependentCard =
    data?.overridableProperties?.allowDependentCard === null && !cardsSetup?.allowDependentCard
      ? null
      : cardsSetup?.allowDependentCard;

  const formatedURL = emailConfiguration?.customEmailLogo
    ? _.last(emailConfiguration?.customEmailLogo?.split(URL_PREFIX)) || ''
    : '';
  const emailConfigurationFields = _.pickBy({
    email_logo_type: emailConfiguration?.emailLogoType,
    email_illustration_id: emailConfiguration?.brandingIllustration
      ? _.toNumber(emailConfiguration?.brandingIllustration)
      : null,
    // is_default_link: emailConfiguration?.isDefaultLinks,
    email_theme: emailConfiguration?.emailTheme,
    email_logo_path: formatedURL,
  });
  const linkManagementFields = _.pickBy({
    about_us_link: linkManagement?.aboutUsLink,
    email_domain: linkManagement?.domainAddress,
    terms_and_conditions_link: linkManagement?.termAndConditionsLink,
    privacy_policy_link: linkManagement?.privacyPolicyLink,
    pricing_consent: linkManagement?.pricingConsent,
  });

  const accountUsageProperties = hasAccessToBankInfo
    ? getFormattedAccountUsage(accountUsageStore, banksList, data)
    : null;
  const isExternalAccountAnable =
    accountUsageStore.administratorFeesBank === NON_ASSIGNABLE_VALUE ||
    accountUsageStore.payrollContributionBank === NON_ASSIGNABLE_VALUE ||
    accountUsageStore.fundingReplenishmentBank === NON_ASSIGNABLE_VALUE;
  const isPrefundInitialDelayDateValid =
    !accountUsageStore?.migrationPreFundStartDate ||
    dateValidatorByFormat(accountUsageStore?.migrationPreFundStartDate, DEFAULT_DATE_FORMAT);
  const prefundInitialDelayDate = isPrefundInitialDelayDateValid
    ? getDateIsoString(accountUsageStore?.migrationPreFundStartDate)
    : null;

  const isCardProcessingStartDateValid =
    !cardsSetup?.cardProcessingStartDate ||
    dateValidatorByFormat(cardsSetup?.cardProcessingStartDate, DEFAULT_DATE_FORMAT);
  const cardProcessingStartDate = isCardProcessingStartDateValid
    ? getDateIsoString(cardsSetup?.cardProcessingStartDate)
    : null;
  return {
    organization_status_type: EmployerStatus.Pending,
    name: employer?.employerName,
    external_identifier: data?.externalIdentifier,
    grandparent_id: data?.grandparentId,
    type: data?.type,
    parent_id: data?.parentId,
    short_name: generalInfoData?.dba || null,
    note: employer?.notes
      ? {
          text: employer.notes,
        }
      : undefined,

    addresses: [
      {
        id: data?.addresses?.[0]?.id,
        line1: address?.street,
        line2: address?.street_2 || null,
        city: address?.city,
        state: address?.state,
        zipcode: address?.zipCode,
        preferred: true,
        country_id: COUNTRY_ID, // constant value
        address_type: ADDRESS_TYPE, // constant value
      },
    ],

    normal_properties: {
      ...(isExternalAccountAnable
        ? {
            external_account_enable: true,
          }
        : {}),
      tax_id: generalInfoData?.taxID || null,
      ...(accountUsageProperties && Object.keys(accountUsageProperties?.accounts).length
        ? { ...accountUsageProperties?.accounts }
        : {}),
      account_manager_id: generalInfoData?.accountManagerId || null,
      implementation_manager_id: generalInfoData?.implementationManagerId || null,
      invoicing_contact_id: primaryContactId || null,
      ...(claimsSettings?.claimNote
        ? {
            claim_note: claimsSettings?.claimNote,
          }
        : {}),
    },
    organization_phone: generalInfoData?.phoneNumber
      ? {
          number: generalInfoData?.phoneNumber,
          phone_extension: generalInfoData?.phoneNumberExtension || null,
        }
      : null,
    business_entity_type: generalInfoData?.businessType || null,
    overridable_properties: {
      allow_manual_contribution: allowAdminContribution,
      allow_scheduled_contribution: allowAssumedContribution,
      participant_portal_link: data?.overridableProperties?.participantPortalLink,
      admin_portal_link: data?.overridableProperties?.adminPortalLink,
      external_link: expressSetupLinks?.externalLink,
      is_external_link: expressSetupLinks?.isExternalLink,
      number_of_days_before_plan_start: cardsSetup?.numberOfDaysBeforePlanStart || null,
      card_offered: cardsSetup?.cardOffered,
      allow_dependent_card: allowDependentCard,
      bin_setup: cardsSetup?.bin || null,
      prin_setup: cardsSetup?.prin || data?.overridableProperties?.prin || null,
      ...(cardsSetup?.recurringCardSwipes
        ? {
            recurring_card_swipes: cardsSetup.recurringCardSwipes,
          }
        : {}),
      push_provisioning: yesOrNoToBoolean(cardsSetup?.pushProvisioning),
      documents_needed_reminder: yesOrNoToBoolean(cardsSetup?.documentsNeededReminder),
      documents_needed_reminder_days:
        cardsSetup?.documentsNeededReminder === YES_KEY
          ? Number(cardsSetup?.documentsNeededReminderDays) || null
          : null,
      suspend_card: yesOrNoToBoolean(cardsSetup?.suspendCard),
      suspend_card_days:
        cardsSetup?.suspendCard === YES_KEY ? Number(cardsSetup?.suspendCardDays) || null : null,
      suspension_threshold: yesOrNoToBoolean(cardsSetup?.suspensionThreshold),
      suspension_threshold_amount:
        cardsSetup?.suspensionThreshold === YES_KEY
          ? Number(cardsSetup?.suspensionThresholdAmount) || null
          : null,
      hsa_custodian_id: generalInfoData?.hsaCustodianId || null,
      depository_bank_config_type: generalInfoData?.depositoryBankConfigType || null,
      id_source_id: employeeId?.eeid || data?.overridableProperties?.idSourceId,

      is_custom_reporting_field_1_enabled: reports?.customReportDisplayedField1,
      is_custom_reporting_field_2_enabled: reports?.customReportDisplayedField2,
      is_custom_reporting_field_3_enabled: reports?.customReportDisplayedField3,
      is_custom_reporting_field_4_enabled: reports?.customReportDisplayedField4,
      is_custom_reporting_field_5_enabled: reports?.customReportDisplayedField5,

      custom_reporting_field_1_name: !reports?.customReportDisplayedField1
        ? null
        : reports?.customReportField1 || null,
      custom_reporting_field_2_name: !reports?.customReportDisplayedField2
        ? null
        : reports?.customReportField2 || null,
      custom_reporting_field_3_name: !reports?.customReportDisplayedField3
        ? null
        : reports?.customReportField3 || null,
      custom_reporting_field_4_name: !reports?.customReportDisplayedField4
        ? null
        : reports?.customReportField4 || null,
      custom_reporting_field_5_name: !reports?.customReportDisplayedField5
        ? null
        : reports?.customReportField5 || null,

      participant_chatbot_link:
        helpInfo?.enableParticipantChatbot && helpInfo?.participantChatbotLink
          ? helpInfo?.participantChatbotLink
          : '',
      admin_chatbot_link:
        helpInfo?.enableAdminChatbot && helpInfo?.adminChatbotLink ? helpInfo?.adminChatbotLink : '',
      zendesk_sso_enabled: typeof helpInfo?.zendeskSsoEnabled === 'boolean'
        ? helpInfo?.zendeskSsoEnabled
        : data?.overridableProperties?.zendeskSsoEnabled,
      working_hours: helpInfo?.workingHours || '',
      customer_service_number: helpInfo?.customerServiceNumber || null,
      customer_service_url_name: helpInfo?.customerServiceUrlName || null,
      customer_service_url_link: helpInfo?.customerServiceUrlLink || null,
      customer_service_email: helpInfo?.customerServiceEmail || null,
      customer_service_is_enabled:
        typeof helpInfo?.customerServiceIsEnabled === 'boolean' ? helpInfo?.customerServiceIsEnabled : null,
      ...(cardsSetup?.cardURL === EMPTY_FIELD_VALUE
        ? {}
        : {
            card_url: cardsSetup?.cardURL || null,
          }),
      ...(cardsSetup?.cardEmployerName === EMPTY_FIELD_VALUE
        ? {}
        : {
            card_employer_name: cardsSetup?.cardEmployerName || null,
          }),
      ...(cardsSetup?.cardPhoneNumber === EMPTY_FIELD_VALUE
        ? {}
        : {
            card_phone_number: cardsSetup?.cardPhoneNumber || null,
          }),
      ...(isCardProcessingStartDateValid
        ? {
            card_processing_start_date: cardProcessingStartDate,
          }
        : {}),
      electronic_funding_mode_id:
        fundingModelPayload?.electronicFundingReplenishment === ''
          ? null
          : fundingModelPayload?.electronicFundingReplenishment ||
            data?.overridableProperties?.electronicFundingModeId ||
            null,
      check_funding_model:
        fundingModelPayload?.checkWriting === ''
          ? null
          : fundingModelPayload?.checkWriting || data?.overridableProperties?.checkFundingModel || null,
      ...(generalInfoData?.themeId
        ? {
            theme_id: generalInfoData?.themeId,
          }
        : {}),
      sso_only: Boolean(generalInfoData?.ssoOnly),
      admin_sso_only: Boolean(generalInfoData?.adminSsoOnly),
      allow_employees_to_edit_address: yesOrNoToBoolean(generalInfoData?.allowEditAddress),
      url: generalInfoData?.url || null,
      communication_channels: communicationChannelStore.communicationChannel || [],
      email_template_type:
        communicationChannelStore.emailTemplate !== '' ? communicationChannelStore.emailTemplate : null,
      organization_communication_categories: communicationCategoryStore.communicationCategory || [],
      ...(fileManagerSettings?.isFileManagerAccessEnabled
        ? {
            is_file_manager_access_enabled: fileManagerSettings?.isFileManagerAccessEnabled,
            is_file_auto_post_enabled: yesOrNoToBoolean(fileManagerSettings?.isFileAutoPostEnabled),
            allowed_file_types: fileManagerSettings?.allowedFileTypes,
            duplicate_check_file_types: fileManagerSettings?.duplicateCheckFileTypes,
          }
        : {
            is_file_manager_access_enabled: fileManagerSettings?.isFileManagerAccessEnabled,
            is_file_auto_post_enabled: false,
            allowed_file_types: [],
            duplicate_check_file_types: [],
          }),

      file_location: !fileManagerSettings?.isSendFiles ? '' : fileManagerSettings?.fileLocation,
      error_report_location: fileManagerSettings?.isSameErrorReportLocation
        ? null
        : fileManagerSettings?.errorReportLocation,

      ...(accountUsageStore.holdPeriod
        ? {
            payroll_contributions_hold_period: _.toNumber(accountUsageStore.holdPeriod),
          }
        : {}),
      ...(technicalSettings?.sessionTimeoutWarn
        ? {
            session_timeout_warn: technicalSettings?.sessionTimeoutWarn,
          }
        : {}),
      ...(technicalSettings?.sessionTimeoutLogout
        ? {
            session_timeout_logout: technicalSettings?.sessionTimeoutLogout,
          }
        : {}),
      ...(claimsSettings?.claimsAssignmentNumber
        ? {
            claims_assignment_number: _.toNumber(claimsSettings.claimsAssignmentNumber),
          }
        : {}),
      ...(accountUsageProperties && Object.keys(accountUsageProperties.methods).length
        ? { ...accountUsageProperties.methods }
        : {}),
      replenishment_frequency_type: accountUsageStore.replenishmentFrequencyType,
      ...(accountUsageStore.replenishmentFrequencyType === FrequencyOptions.DAILY
        ? {
            replenishment_day_number: DAILY_FREQUENCY,
          }
        : {
            replenishment_day_number:
              accountUsageStore.replenishmentFrequencyType === FrequencyOptions.WEEKLY &&
              accountUsageStore.replenishmentDayOfWeek
                ? daysOfWeek[accountUsageStore.replenishmentDayOfWeek]
                : accountUsageStore.replenishmentCustomDay ||
                  data?.overridableProperties?.replenishmentDayNumber,
          }),
      prefund_replenishment_percent: accountUsageStore.preFundPercent
        ? _.toNumber(accountUsageStore.preFundPercent)
        : data?.overridableProperties?.replenishmentPercent,
      prefund_replenishment_min_value: accountUsageStore.preFundMinimumValue
        ? _.toNumber(accountUsageStore.preFundMinimumValue)
        : data?.overridableProperties?.replenishmentMinValue,
      prefund_replenishment_creep_min: accountUsageStore.preFundMinimumThreshold
        ? _.toNumber(accountUsageStore.preFundMinimumThreshold)
        : data?.overridableProperties?.replenishmentCreepMin,
      prefund_replenishment_creep_percent: accountUsageStore.preFundElectionChangePercentage
        ? _.toNumber(accountUsageStore.preFundElectionChangePercentage)
        : data?.overridableProperties?.replenishmentCreepPercent,
      ...(accountUsageStore?.eeContributionsHoldPeriod
        ? {
            ee_contributions_hold_period: _.toNumber(accountUsageStore?.eeContributionsHoldPeriod),
          }
        : {}),
      ...(isPrefundInitialDelayDateValid && accountUsageStore?.allowPrefund
        ? {
            prefund_initial_delay_date: prefundInitialDelayDate,
          }
        : {}),
      ...reimbursementPayload,
      ...emailConfigurationFields,
      allow_prefund: !!accountUsageStore?.allowPrefund,
      ...(plansRenewalStore?.allowRenewals ? { allow_renewals: plansRenewalStore?.allowRenewals } : {}),
      orphan_organization_id: orphanConfiguration?.orphanEmployer
        ? _.toNumber(orphanConfiguration?.orphanEmployer)
        : undefined,
      orphan_start_timing_days: orphanConfiguration?.startOrphanProcessDays
        ? _.toNumber(orphanConfiguration?.startOrphanProcessDays)
        : undefined,
      is_organization_orphan: orphanConfiguration?.isEmployerOrphan,
      ...linkManagementFields,
      statement_type: statementsSettingsStore?.statementType,
      statement_frequency_type: statementsSettingsStore?.statementFrequencyType,

      ...(statementsSettingsStore?.statementType === StatementType.PAPER_AND_ELECTRONIC
        ? {
            paper_statement_fees_paid_by: statementsSettingsStore?.paperStatementFeesPaidBy,
            ...(statementsSettingsStore?.paperStatementFeesPaidBy === StatementFeePaidByType.PARTICIPANT
              ? {
                  paper_statement_fees_amount: statementsSettingsStore?.paperStatementFeesAmount,
                }
              : {}),
          }
        : {}),
      ai_adjudication: aiAdjudicationStore?.aiAdjudication,
      ai_confidence_level: aiAdjudicationStore?.aiConfidenceLevel,
      is_sigis_auto_approve_enabled: aiAdjudicationStore?.isSigisAutoApproveEnabled,
      require_participant_mfa:
        MultiFactorAuthenticationStore?.requireParticipantMfa ??
        data?.overridableProperties?.requireParticipantMfa,
      require_admin_mfa:
        MultiFactorAuthenticationStore?.requireAdminMfa ?? data?.overridableProperties?.requireAdminMfa,
      account_lockout_time:
        MultiFactorAuthenticationStore?.accountLockoutTime || data?.overridableProperties?.accountLockoutTime,
      ...(mobileAppCustomizationState?.participantMobileApp
        ? { partner_mobile_app: mobileAppCustomizationState?.participantMobileApp }
        : {}),
      ...(mobileAppCustomizationState?.googlePlayLink &&
      mobileAppCustomizationState?.participantMobileApp === PartnerMobileApp.PARTNER_OWN_APP
        ? { partner_app_android_link: mobileAppCustomizationState?.googlePlayLink }
        : {}),
      ...(mobileAppCustomizationState?.appStoreLink &&
      mobileAppCustomizationState?.participantMobileApp === PartnerMobileApp.PARTNER_OWN_APP
        ? { partner_app_ios_link: mobileAppCustomizationState?.appStoreLink }
        : {}),
      ...(mobileAppCustomizationState?.mobileAppTheme &&
      mobileAppCustomizationState?.participantMobileApp === PartnerMobileApp.ELEVATE_APP
        ? { mobile_app_theme_id: mobileAppCustomizationState?.mobileAppTheme }
        : {}),
      contribution_push_give_up_days: accountUsageStore?.contributionPushGiveUpDays,
      payroll_contributions_hold_period: accountUsageStore?.payrollContributionHoldPeriod,
      balance_adjustment_hold_period: accountUsageStore.balanceAdjustmentHoldPeriod
        ? _.toNumber(accountUsageStore.balanceAdjustmentHoldPeriod)
        : undefined,
      balance_adjustment_push_give_up_days: accountUsageStore.balanceAdjustmentPushGiveUpDays
        ? _.toNumber(accountUsageStore.balanceAdjustmentPushGiveUpDays)
        : undefined,
      substantiation_rate: metrics?.substantiationRate === YES_KEY ? true : false,
      automated_org_lock_enabled:
        accountUsageStore?.automatedOrgLockEnabled ?? data?.overridableProperties?.automatedOrgLockEnabled,
      fraud_single_claim_threshold: !!fraudThresholdStore?.isFraudSingleClaimThresholdEnabled,
      fraud_single_claim_threshold_amount: fraudThresholdStore?.isFraudSingleClaimThresholdEnabled
        ? Number(fraudThresholdStore?.fraudSingleClaimThresholdAmount) || null
        : null,
      fraud_daily_threshold: !!fraudThresholdStore?.isFraudDailyThresholdEnabled,
      fraud_daily_threshold_amount: fraudThresholdStore?.isFraudDailyThresholdEnabled
        ? Number(fraudThresholdStore?.fraudDailyThresholdAmount) || null
        : null,
      fraud_weekly_threshold: !!fraudThresholdStore?.isFraudWeeklyThresholdEnabled,
      fraud_weekly_threshold_amount: fraudThresholdStore?.isFraudWeeklyThresholdEnabled
        ? Number(fraudThresholdStore?.fraudWeeklyThresholdAmount) || null
        : null,
    },
  };
};

export const SAVE_EMPLOYER = 'saveEmployer';

const useSaveEmployerQuery = (employerId: string) => {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation(
    (newValue: object) => api.put<IOrganizationInfoDto>(`${API_URL.ORGANIZATIONS}/${employerId}`, newValue),
    { retry: false }
  );

  const save = async (value: object) => {
    const result = await mutateAsync(value);
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY],
    });
    queryClient.invalidateQueries({
      queryKey: [API_URL.ORGANIZATIONS_BY_ORGANIZATION_ID(employerId)],
    });
    return result.data;
  };

  return {
    save,
  };
};

export default useSaveEmployerQuery;
